import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import { inAppNotificationService } from '../services/inAppNotificationService';

const NotificationCenter = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);

  const handleNewNotification = (newNotification) => {
    const formattedNotification = {
      id: newNotification?.id,
      userId: newNotification?.user_id,
      emailLogId: newNotification?.email_log_id,
      title: newNotification?.title,
      message: newNotification?.message,
      notificationType: newNotification?.notification_type,
      isRead: newNotification?.is_read,
      readAt: newNotification?.read_at,
      metadata: newNotification?.metadata,
      createdAt: newNotification?.created_at,
      updatedAt: newNotification?.updated_at
    };

    setNotifications(prev => [formattedNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
  };

  const handleUpdatedNotification = (updated) => {
    setNotifications(prev =>
      prev?.map(n =>
        n?.id === updated?.id
          ? {
              ...n,
              isRead: updated?.is_read,
              readAt: updated?.read_at,
              updatedAt: updated?.updated_at
            }
          : n
      )
    );
    if (updated?.is_read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
      loadUnreadCount();

      // Subscribe to real-time updates
      const channel = inAppNotificationService?.subscribeToNotifications(
        handleNewNotification,
        handleUpdatedNotification
      );

      return () => {
        inAppNotificationService?.unsubscribe(channel);
      };
    }
  }, [isOpen, filter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const filters = {
        isRead: filter === 'unread' ? false : filter === 'read' ? true : undefined,
        limit: 50
      };
      const data = await inAppNotificationService?.getNotifications(filters);
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const count = await inAppNotificationService?.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await inAppNotificationService?.markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await inAppNotificationService?.markAllAsRead();
      setNotifications(prev => prev?.map(n => ({ ...n, isRead: true, readAt: new Date()?.toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await inAppNotificationService?.deleteNotification(notificationId);
      setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
      const deletedNotification = notifications?.find(n => n?.id === notificationId);
      if (deletedNotification && !deletedNotification?.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'email_sent':
        return { name: 'Send', color: 'text-[#002B45]' };
      case 'email_delivered':
        return { name: 'CheckCircle', color: 'text-green-600' };
      case 'email_bounced':
        return { name: 'AlertTriangle', color: 'text-yellow-600' };
      case 'email_failed':
        return { name: 'XCircle', color: 'text-red-600' };
      case 'email_opened':
        return { name: 'Mail', color: 'text-purple-600' };
      case 'email_clicked':
        return { name: 'MousePointer', color: 'text-indigo-600' };
      default:
        return { name: 'Bell', color: 'text-gray-600' };
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={onClose}
      />

      {/* Notification Panel */}
      <div className="fixed top-0 right-0 h-full w-full md:w-96 bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Icon name="Bell" size={20} className="text-[#002B45]" />
            <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 p-4 border-b border-gray-200">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filter === 'all' ? 'bg-[#002B45] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filter === 'unread' ? 'bg-[#002B45] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Unread
          </button>
          <button
            onClick={() => setFilter('read')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filter === 'read' ? 'bg-[#002B45] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Read
          </button>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="ml-auto text-sm text-[#002B45] hover:text-[#003d5c] font-medium"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002B45]" />
            </div>
          ) : notifications?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Icon name="Bell" size={32} className="mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {notifications?.map(notification => {
                const icon = getNotificationIcon(notification?.notificationType);
                return (
                  <div
                    key={notification?.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      !notification?.isRead ? 'bg-[#E6F0F5]' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 ${icon?.color}`}>
                        <Icon name={icon?.name} size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-sm font-semibold text-gray-900">
                            {notification?.title}
                          </h3>
                          {!notification?.isRead && (
                            <div className="w-2 h-2 bg-[#002B45] rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification?.message}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(notification?.createdAt)}
                          </span>
                          {!notification?.isRead && (
                            <button
                              onClick={() => handleMarkAsRead(notification?.id)}
                              className="text-xs text-[#002B45] hover:text-[#003d5c] font-medium"
                            >
                              Mark as read
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteNotification(notification?.id)}
                            className="text-xs text-red-600 hover:text-red-700 font-medium ml-auto"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationCenter;